import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useHolidayService from '../../app/services/hooks/useHolidayService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import SelectTwo from '../../components/SelectTwo';

interface HolidayFormData {
    name: string;
    description: string;
    date: string;
    recurrence_type: string;
    day_of_week: number | null;
}

const defaultFormData: HolidayFormData = {
    name: '',
    description: '',
    date: '',
    recurrence_type: 'unique',
    day_of_week: null
};

const recurrenceTypeOptions = [
    { value: 'unique', label: 'Único (una sola vez)' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'annual', label: 'Anual' }
];

const dayOfWeekOptions = [
    { value: 0, label: 'Lunes' },
    { value: 1, label: 'Martes' },
    { value: 2, label: 'Miércoles' },
    { value: 3, label: 'Jueves' },
    { value: 4, label: 'Viernes' },
    { value: 5, label: 'Sábado' },
    { value: 6, label: 'Domingo' }
];

const HolidayForm: React.FC = () => {
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const isEdit = Boolean(id);
    
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const {
        storeHoliday,
        showHoliday,
        updateHoliday,
        fetchingStoreHoliday,
        fetchingShowHoliday,
        fetchingUpdateHoliday
    } = useHolidayService();
    
    const [formData, setFormData] = useState<HolidayFormData>(defaultFormData);
    
    useEffect(() => {
        if (isEdit && id) {
            loadHoliday(parseInt(id));
        }
    }, [id]);
    
    const loadHoliday = (holidayId: number) => {
        showHoliday(holidayId, {
            onSuccess: (res: any) => {
                const holiday = res.data.holiday;
                setFormData({
                    name: holiday.name || '',
                    description: holiday.description || '',
                    date: holiday.date || '',
                    recurrence_type: holiday.recurrence_type || 'unique',
                    day_of_week: holiday.day_of_week
                });
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cargar el feriado');
                history.push('/intranet/holidays');
            }
        });
    };
    
    const handleInputChange = (field: keyof HolidayFormData) => (value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Si cambia el tipo de recurrencia y no es semanal, limpiar día de la semana
        if (field === 'recurrence_type' && value !== 'weekly') {
            setFormData(prev => ({ ...prev, [field]: value, day_of_week: null }));
        }
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        const dataToSend: any = {
            name: formData.name,
            description: formData.description,
            date: formData.date,
            recurrence_type: formData.recurrence_type
        };
        
        // Solo incluir day_of_week si es semanal
        if (formData.recurrence_type === 'weekly') {
            dataToSend.day_of_week = formData.day_of_week;
        }
        
        const callbacks = {
            onSuccess: (res: any) => {
                toast.success(res.message || `Feriado ${isEdit ? 'actualizado' : 'creado'} correctamente`);
                setErrors({});
                history.push('/intranet/holidays');
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al guardar el feriado');
            },
            onFieldError: (res: any) => {
                setErrors(res.data);
                toast.error('Por favor corrige los errores en el formulario');
            }
        };
        
        const action = isEdit && id
            ? () => updateHoliday(parseInt(id), dataToSend, callbacks)
            : () => storeHoliday(dataToSend, callbacks);
        
        action();
    };
    
    const handleCancel = () => {
        history.push('/intranet/holidays');
    };
    
    const isLoading = fetchingShowHoliday || fetchingStoreHoliday || fetchingUpdateHoliday;
    
    // Loading inicial
    if (isEdit && fetchingShowHoliday) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Feriados', path: '/intranet/holidays' },
                    { label: isEdit ? 'Editar Feriado' : 'Nuevo Feriado', isCurrent: true }
                ]}
                title={isEdit ? 'Editar Feriado' : 'Nuevo Feriado'}
                description={isEdit ? 'Modifica los datos del feriado' : 'Configura un nuevo día feriado en el sistema'}
            />
            
            <DefaultCard>
                <form onSubmit={handleSubmit}>
                    {/* Información Básica */}
                    <div className="row mb-4">
                        <div className="col-12 mb-3">
                            <h5 className="text-primary border-bottom pb-2 mb-3">
                                <i className="fas fa-info-circle me-2"></i>
                                Información del Feriado
                            </h5>
                        </div>
                        
                        <div className="col-md-6">
                            <InputField
                                label="Nombre del Feriado"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                onFocus={() => onFocusRemove('name')}
                                error={fieldErrorMessage('name')}
                                placeholder="Ej: Año Nuevo, Fiestas Patrias, etc."
                                required
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <InputField
                                label="Fecha"
                                type="date"
                                value={formData.date}
                                onChange={handleInputChange('date')}
                                onFocus={() => onFocusRemove('date')}
                                error={fieldErrorMessage('date')}
                                required
                            />
                        </div>
                        
                        <div className="col-12 mt-3">
                            <TextAreaField
                                label="Descripción (opcional)"
                                value={formData.description}
                                onChange={handleInputChange('description')}
                                onFocus={() => onFocusRemove('description')}
                                error={fieldErrorMessage('description')}
                                placeholder="Agrega una descripción o nota sobre este feriado..."
                                rows={3}
                            />
                        </div>
                    </div>
                    
                    {/* Configuración de Recurrencia */}
                    <div className="row mb-4">
                        <div className="col-12 mb-3">
                            <h5 className="text-primary border-bottom pb-2 mb-3">
                                <i className="fas fa-sync-alt me-2"></i>
                                Configuración de Recurrencia
                            </h5>
                            <p className="text-muted small mb-3">
                                Define si el feriado ocurre una sola vez o se repite periódicamente.
                            </p>
                        </div>
                        
                        {/* Tarjetas de tipo de recurrencia */}
                        <div className="col-12 mb-4">
                            <div className="row g-3">
                                {recurrenceTypeOptions.map((option) => (
                                    <div className="col-md-3" key={option.value}>
                                        <div
                                            className={`card h-100 cursor-pointer transition-all ${
                                                formData.recurrence_type === option.value
                                                    ? 'border-primary shadow-sm'
                                                    : 'border-secondary'
                                            }`}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                borderWidth: formData.recurrence_type === option.value ? '2px' : '1px'
                                            }}
                                            onClick={() => handleInputChange('recurrence_type')(option.value)}
                                        >
                                            <div className="card-body text-center py-4">
                                                <div 
                                                    className={`rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center ${
                                                        formData.recurrence_type === option.value
                                                            ? 'bg-primary text-white'
                                                            : 'bg-light text-secondary'
                                                    }`}
                                                    style={{ width: '50px', height: '50px' }}
                                                >
                                                    <i className={`fas ${
                                                        option.value === 'unique' ? 'fa-calendar-day' :
                                                        option.value === 'weekly' ? 'fa-calendar-week' :
                                                        option.value === 'monthly' ? 'fa-calendar-alt' :
                                                        'fa-calendar'
                                                    } fa-lg`}></i>
                                                </div>
                                                <h6 className={`mb-1 ${
                                                    formData.recurrence_type === option.value
                                                        ? 'text-primary'
                                                        : 'text-secondary'
                                                }`}>
                                                    {option.label}
                                                </h6>
                                                <small className="text-muted">
                                                    {option.value === 'unique' && 'Ocurre solo en la fecha indicada'}
                                                    {option.value === 'weekly' && 'Se repite cada semana'}
                                                    {option.value === 'monthly' && 'Se repite cada mes'}
                                                    {option.value === 'annual' && 'Se repite cada año'}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {fieldErrorMessage('recurrence_type') && (
                                <div className="text-danger small mt-2">
                                    {fieldErrorMessage('recurrence_type')}
                                </div>
                            )}
                        </div>
                        
                        {/* Selector de día de la semana (solo para semanal) */}
                        {formData.recurrence_type === 'weekly' && (
                            <div className="col-md-6">
                                <SelectTwo
                                    label="Día de la Semana"
                                    inputValue={formData.day_of_week}
                                    onChange={(option: any) => handleInputChange('day_of_week')(option?.value ?? null)}
                                    options={dayOfWeekOptions}
                                    placeholder="Selecciona el día..."
                                />
                                {fieldErrorMessage('day_of_week') && (
                                    <div className="text-danger small mt-1">
                                        {fieldErrorMessage('day_of_week')}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Información adicional según el tipo */}
                        <div className="col-12 mt-3">
                            <div className="alert alert-info d-flex align-items-center" role="alert">
                                <i className="fas fa-info-circle me-2"></i>
                                <div>
                                    {formData.recurrence_type === 'unique' && (
                                        <span>Este feriado ocurrirá <strong>solo una vez</strong> en la fecha seleccionada.</span>
                                    )}
                                    {formData.recurrence_type === 'weekly' && (
                                        <span>Este feriado se repetirá <strong>cada semana</strong> en el día seleccionado.</span>
                                    )}
                                    {formData.recurrence_type === 'monthly' && (
                                        <span>Este feriado se repetirá <strong>cada mes</strong> en el día {formData.date ? new Date(formData.date).getDate() : '...'} del mes.</span>
                                    )}
                                    {formData.recurrence_type === 'annual' && (
                                        <span>Este feriado se repetirá <strong>cada año</strong> en la misma fecha (día y mes).</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Botones de acción */}
                    <div className="row">
                        <div className="col-12 d-flex justify-content-end gap-2">
                            <ButtonGeneral
                                type="button"
                                onClick={handleCancel}
                                title="Cancelar"
                                variant="secondary"
                                size="medium"
                            />
                            <ButtonGeneral
                                type="submit"
                                onClick={() => handleSubmit({ preventDefault: () => undefined } as React.FormEvent)}
                                title={isEdit ? 'Guardar Cambios' : 'Crear Feriado'}
                                variant="primary"
                                size="medium"
                                loading={isLoading}
                                icon="fa-save"
                                iconPosition="left"
                            />
                        </div>
                    </div>
                </form>
            </DefaultCard>
        </div>
    );
};

export default HolidayForm;

